import { z } from "zod";

/**
 * What a WebFetch measured about its own request, projected from the SDK's
 * `tool_use_result` on the `user` message that settles the call.
 *
 * The SDK hands a tool's full Output object beside the string the model was
 * shown, and for a WebFetch that object carries the request's own outcome —
 * the HTTP response code and its text, how many bytes came back, and how long
 * the fetch and the prompt applied to it took. None of that is recoverable
 * from the text: a fetch that answered 404 succeeded as a tool call, so the
 * string the model saw reads like prose about a missing page and `isError` is
 * unset. A reader holding the code can say the page failed to fetch; a reader
 * holding only the prose has to guess from its wording.
 *
 * **The payload's own key set is the discriminator, because nothing else is.**
 * Nothing in a `tool_use_result` names the tool that produced it, and no field
 * here is unique on its own — `durationMs` is shared with Glob's payload, and
 * `code` with Bash's. What separates a WebFetch is the whole declared set
 * arriving together, so the schema below is `.strict()` over every required key
 * the SDK declares on `WebFetchOutput`, including the two this wire does not
 * carry onward. Measured across the Agent SDK research corpus
 * (https://github.com/yann-lauwers/claude-agent-sdk-research), twenty distinct
 * `tool_use_result` shapes reach this transport and none of them matches it.
 *
 * Relaxing a key to optional would open that gate with no strictness to catch
 * it, which is the failure `AgentDispatchResultSchema` records for `agentId`.
 *
 * **Reachability is inherited from a sibling rather than measured directly, and
 * that is stated rather than implied.** Typing is gated on reachability, and
 * the corpus holds zero successful WebFetch captures: both
 * runs that called it settled `is_error` with `tool_use_result` a bare string
 * (`error:100000f7:SSL routines:…WRONG_VERSION_NUMBER` — an HTTPS request
 * meeting a non-TLS port, which is the capture harness's proxy rather than
 * anything about this transport). That is an ARMED probe that failed at the
 * network layer, not an unarmed zero. What carries the reachability is
 * WebSearch, the sibling on the identical envelope: eleven captures, every one
 * delivering its full declared `WebSearchOutput` object on `tool_use_result`.
 *
 * The MCP counter-example does not reach this case. That shape was
 * refused because a positive capture showed the runner used a DIFFERENT
 * transport than the declared type belonged to. There is no such evidence here
 * — a WebFetch arrives as an ordinary `tool_use` and settles with an ordinary
 * `tool_result`, which is the envelope the sibling's Output object rides.
 *
 * The shape below is therefore built from the vendor's declaration at the
 * pinned SDK version, stated rather than left implied. A real capture is
 * checked against it rather than replacing it.
 */
const WebFetchOutputShape = z
  .object({
    /** Size of the fetched content, before the prompt was applied to it. */
    bytes: z.number().nonnegative(),
    /**
     * The HTTP response code. Not constrained to a range: a code this build has
     * no opinion about is still what the fetch received, and refusing it would
     * cost the whole frame rather than the field.
     */
    code: z.number(),
    /** The response code's own text — the "Not Found" beside a 404. */
    codeText: z.string(),
    result: z.string(),
    /** How long fetching and processing the content took, end to end. */
    durationMs: z.number().nonnegative(),
    url: z.string(),
    /**
     * Declared so a payload carrying it is still recognised, and left unparsed
     * because nothing here reads it. A strict shape refuses an undeclared key,
     * so omitting this would make an artifact-backed fetch unrecognisable —
     * and constraining its contents would make the vendor's next change to a
     * nested bag we never read cost us the whole projection.
     */
    artifactRead: z.unknown().optional()
  })
  .strict();

/**
 * The four fields that travel, projected from the object above.
 *
 * `result` and `url` are deliberately not carried. `result` is the processed
 * answer the model was shown, which the tool result's own `output` already
 * holds — a second copy would roughly double a line the transport drops WHOLE
 * rather than truncating, and the line it would drop is the one that settles
 * the call. `url` is model-authored input, already on the wire as this call's
 * own arguments; re-sending it as a host-observed fact puts one value in two
 * vocabularies for a reader to reconcile.
 *
 * `artifactRead` is declared optional by the SDK and describes a stored
 * artifact rather than the fetch, so it is recognised above and travels no
 * further.
 */
export const WebFetchResultSchema = WebFetchOutputShape.pick({
  code: true,
  codeText: true,
  bytes: true,
  durationMs: true
});

export type WebFetchResult = z.infer<typeof WebFetchResultSchema>;

/**
 * It lives beside the schema rather than in the runner source because the
 * discriminator and the shape it guards are one decision, and splitting them
 * lets one move without the other.
 *
 * `readAgentDispatchResult` sits in the runner instead, and the asymmetry is
 * deliberate: that reader writes a drift breadcrumb to `process.stderr`, which
 * no browser-facing package may carry, and it truncates a report at an edge
 * this one has no equivalent of.
 */
export function readWebFetchResult(toolUseResult: unknown): WebFetchResult | null {
  // Refuse on one required key before entering the parse. `codeText` is required
  // below, so a payload without it could never have parsed — and this is the
  // common path rather than the rare one: 887 object-shaped `tool_use_result`
  // payloads reach this transport across the capture corpus and none but a
  // fetch's carries the key. A strict refusal builds an error carrying an issue
  // per missing key, so the branch taken almost every time is the expensive one.
  // The sibling reader guards the same way, on `agentId`.
  if (typeof toolUseResult !== "object" || toolUseResult === null) return null;
  if (!("codeText" in toolUseResult) || typeof toolUseResult.codeText !== "string") return null;

  const recognised = WebFetchOutputShape.safeParse(toolUseResult);
  if (!recognised.success) return null;

  const { code, codeText, bytes, durationMs } = recognised.data;
  return { code, codeText, bytes, durationMs };
}
